import { useNavigation } from '@react-navigation/native';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TouchableOpacity, Pressable } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { snapPoint } from 'react-native-redash';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { DateRangePickerMobile } from '@components/DateRangePicker/DateRangePickerMobile';
import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import { Box, ShadowBox, Text } from '@components/Restyle';
import { FormValues } from '@components/Tasks/TaskFormStack';
import { User } from '@graphql/generated';
import useChatInput from '@hooks/useChatInput';
import Images from '@themes/images';
import theme from '@themes/theme';
import { localDateFromUTCDate } from '@utils/formatters/date';

export type ModalItemType = {
  title: string;
  iconName?: keyof typeof Images;
  label?: string;
  onSelect: () => void;
  color?: keyof typeof theme.colors;
  titleColor?: keyof typeof theme.colors;
  comingSoon?: boolean;
  disable?: boolean;
  accessibilityLabel?: string;
  trailingIconName?: keyof typeof Images;
  users?: User[];
};

interface TaskEditDatesModalProps {
  goBack?: () => void;
}

export const TaskEditDatesModal: React.FC<TaskEditDatesModalProps> = ({
  goBack,
}) => {
  const { values, setFieldValue, submitForm, isSubmitting, setSubmitting } =
    useFormikContext<FormValues>();

  const { startDate, dueDate } = values;

  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const { t } = useTranslation();
  const [height, setHeight] = useState<number>(0);
  const { setModalHeight } = useChatInput();

  const translateY = useSharedValue(0);

  const pan = Gesture.Pan()
    .onUpdate(({ translationY }) => {
      if (translationY < 0) {
        translateY.value = translationY / 10;
      } else {
        translateY.value = translationY;
      }
    })
    .onEnd(({ translationY, velocityY }) => {
      const shouldGoBack =
        snapPoint(translationY, velocityY, [0, height]) === height;
      if (shouldGoBack) {
        runOnJS(navigation.pop)();
      } else {
        translateY.value = withTiming(0);
      }
    });

  const style = useAnimatedStyle(() => {
    return {
      transform: [{ translateY: translateY.value }],
    };
  });

  const [dueDateRange, setDueDateRange] = useState<DateRange>({
    start: startDate ? dayjs(startDate) : undefined,
    end: dueDate ? dayjs(dueDate) : undefined,
  } as DateRange);

  const [pickerType, setPickerType] = useState<'start' | 'end' | 'range'>(
    'range'
  );
  return (
    <Box
      pt='m'
      style={{
        position: `absolute`,
        bottom: 0,
        left: 0,
        right: 0,
        top: 0,
        justifyContent: 'flex-end',
        backgroundColor: 'transparent',
      }}>
      <Pressable
        style={StyleSheet.absoluteFill}
        onPress={() => (goBack ? goBack() : navigation.goBack())}
        accessibilityLabel='Close modal'
      />
      <GestureDetector gesture={pan}>
        <Animated.View style={style}>
          <Box
            style={[styles.belowModalWhiteSpace, { backgroundColor: 'white' }]}
          />
          <ShadowBox
            variant='dark'
            borderTopLeftRadius='l'
            borderTopRightRadius='l'
            backgroundColor='white'
            paddingTop='m'
            // paddingHorizontal='xs'
            paddingVertical='l'
            style={{
              backgroundColor: 'white',
            }}
            onLayout={(event) => {
              const { height } = event.nativeEvent.layout;
              setHeight(height);
              setModalHeight(height);
            }}>
            <Box style={{ paddingBottom: insets.bottom }} height={536}>
              <TouchableOpacity
                disabled={false}
                onPress={() => (goBack ? goBack() : navigation.goBack())}>
                <Box
                  backgroundColor='grey03'
                  height={4}
                  width={56}
                  borderRadius='xxxs'
                  alignItems='center'
                  alignSelf='center'
                />
              </TouchableOpacity>

              <Box mt='xs' alignItems='center'>
                <Box height={48} justifyContent='center'>
                  <Text variant='labelEmphasized' textAlign='center'>
                    {t('models:projects.taskList.selectDates')}
                  </Text>
                </Box>

                <Box
                  flexDirection='row'
                  paddingHorizontal='l'
                  height={64}
                  borderBottomWidth={1}
                  borderBottomColor='grey02'
                  alignItems='center'>
                  <TouchableOpacity
                    style={{
                      flex: 1,
                      marginRight: theme.spacing.m,
                      flexDirection: 'row',
                    }}
                    onPress={() => {
                      setPickerType('start');
                    }}>
                    <Box
                      flex={1}
                      borderRadius='m'
                      height={32}
                      justifyContent='center'
                      borderWidth={1}
                      backgroundColor={
                        pickerType === 'start' ? 'textPrimary' : 'white'
                      }
                      borderColor={
                        pickerType === 'start' ? 'textPrimary' : 'grey02'
                      }>
                      <Text
                        variant='labelSmall'
                        textAlign='center'
                        color={pickerType === 'start' ? 'white' : 'black'}>
                        {dueDateRange.start
                          ? t('format:monthAndDay', {
                              val: localDateFromUTCDate(
                                dueDateRange.start.toDate()
                              ),
                            })
                          : 'Start Date'}
                      </Text>
                    </Box>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={{ flex: 1, flexDirection: 'row' }}
                    onPress={() => {
                      setPickerType('end');
                    }}>
                    <Box
                      flex={1}
                      justifyContent='center'
                      borderRadius='m'
                      height={32}
                      borderWidth={1}
                      backgroundColor={
                        pickerType === 'end' ? 'textPrimary' : 'white'
                      }
                      borderColor={
                        pickerType === 'end' ? 'textPrimary' : 'grey02'
                      }>
                      <Text
                        variant='labelSmall'
                        textAlign='center'
                        color={pickerType === 'end' ? 'white' : 'black'}>
                        {dueDateRange.end
                          ? t('format:monthAndDay', {
                              val: localDateFromUTCDate(
                                dueDateRange.end.toDate()
                              ),
                            })
                          : 'Due Date'}
                      </Text>
                    </Box>
                  </TouchableOpacity>
                </Box>
              </Box>
              <DateRangePickerMobile
                datePickerType={pickerType}
                setDatePickerType={setPickerType}
                onSelectEndDate={(date) => {
                  setDueDateRange((pre) => ({ ...pre, end: date }));
                  setFieldValue('dueDate', date?.toISOString() ?? null);
                }}
                onSelectStartDate={(date) => {
                  setDueDateRange((pre) => ({ ...pre, start: date }));
                  setFieldValue('startDate', date?.toISOString() ?? null);
                }}
                onSelect={async () => {
                  await submitForm();
                  setSubmitting(false);
                }}
                isSubmitting={isSubmitting}
                start={dueDateRange.start?.toDate()}
                end={dueDateRange.end?.toDate()}
              />
            </Box>
          </ShadowBox>
        </Animated.View>
      </GestureDetector>
    </Box>
  );
};

const styles = StyleSheet.create({
  belowModalWhiteSpace: {
    ...StyleSheet.absoluteFillObject,
    top: `100%`,
    height: theme.spacing.listFooter,
    backgroundColor: theme.colors.white,
    marginTop: -theme.spacing.xs,
    zIndex: 2,
  },
});
